import { useStableArray } from '../hooks/useStableArray';
import type { DropdownState, FocusedElement } from '../state/types';
import { ARIA_LABELS } from '../types';
import { ClearButton } from './ClearButton';
import {
    SymbolPickerMenu,
    SymbolPickerMenuTrigger,
    SymbolPickerMenuContent,
    SymbolPickerMenuItem,
    SymbolPickerMenuHeader,
} from './SymbolPickerMenu';
import { useSignals } from '@preact/signals-react/runtime';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@thinkalpha/ui-core/utils';
import type { SecurityMasterEntry } from '@thinkalpha/ui-types/contracts/quant';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useSecurityMasterEntries, useSecurityMasterEntry } from 'src/features/security-master';
import { useEvent } from 'src/hooks/useEvent';
import { getSymbolSearchServiceQuery, selectUniqueSymbols } from 'src/queries/symbolSearchService';
import type { RecentlyUsedSymbolsService } from 'src/services/RecentlyUsedSymbolsService';
import type { SymbolSearchService } from 'src/services/SymbolSearchService';

const inputErrorClass = 'bg-invalid/30 border-invalid/70 focus-visible:outline';

const SCROLL_THRESHOLD_ITEMS = 10;

type SymbolResult = {
    symbol: string;
    info?: SecurityMasterEntry;
};

type SymbolListItemType = 'header' | 'symbol' | 'recentSymbol';

interface SymbolListItem {
    type: SymbolListItemType;
    symbol?: string;
    info?: SecurityMasterEntry;
    headerText?: string;
    index?: number;
}

export type SymbolPickerProps = {
    clearable?: boolean;
    disabled?: boolean;
    placeholder?: string;
    inputSize?: 'small' | 'medium' | 'large';
    className?: string;
    tabIndex?: number;
    recentlyUsedSymbolsService: RecentlyUsedSymbolsService;
    symbolSearchService: SymbolSearchService;
    onSelect: (symbol: string) => void;
    onClear?: () => void;
    isInvalidSymbol?: boolean;
    // State machine props - required for proper operation
    dropdownOpen: boolean;
    value: string;
    dropdown: DropdownState;
    onDropdownSearch: (term: string) => void;
    onDropdownItems: (element: FocusedElement, items: any[]) => void;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
    onBlur?: (event: React.FocusEvent) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
};

export interface SymbolInputRef {
    insertAndFocus(text: string): void;
    setSearchText(text: string): void;
    clear(): void;
    searchText: string;
}

export const SymbolInput = forwardRef<SymbolInputRef, SymbolPickerProps>(function SymbolFinder(
    {
        dropdownOpen,
        value,
        clearable,
        disabled,
        placeholder = 'Symbol',
        className,
        tabIndex = -1,
        recentlyUsedSymbolsService,
        symbolSearchService,
        onSelect,
        onClear,
        isInvalidSymbol,
        dropdown,
        onDropdownSearch,
        onDropdownItems,
        onDropdownActiveIndex,
        onFocus,
        onBlur,
        onKeyDown,
    },
    _ref,
) {
    useSignals();

    // State to track if the input is focused
    const [isFocused, setIsFocused] = useState(false);

    // Ref for the scrollable element in the dropdown
    const virtualScrollRef = useRef<HTMLDivElement>(null);
    // Ref for the dialog element
    const dialogRef = useRef<HTMLDivElement>(null);

    const { data: symbolsMatchingPrefixRaw = [] } = useQuery({
        ...getSymbolSearchServiceQuery(dropdown.searchTerm, symbolSearchService),
        select: selectUniqueSymbols,
    });

    // Stabilize the array based on content, not reference
    const symbolsMatchingPrefix = useStableArray(symbolsMatchingPrefixRaw, (item) => item.symbol);

    const { data: directSymbolMatch } = useSecurityMasterEntry(dropdown.searchTerm);

    // get many results, used for recent items
    const { data: recentResultsEntriesRaw = [] } = useSecurityMasterEntries(
        recentlyUsedSymbolsService.symbols.value.map((x) => x.symbol),
    );

    // Stabilize the array based on content, not reference
    const recentResultsEntries = useStableArray(recentResultsEntriesRaw, (entry) => entry.todayTicker);

    const recentResults = useMemo<SymbolResult[]>(() => {
        if (!recentResultsEntries) return [];
        const searchTermLower = dropdown.searchTerm?.toLowerCase() || '';
        return recentResultsEntries
            .filter((x) => {
                const todayTicker = x.todayTicker?.toLowerCase();
                return todayTicker?.startsWith(searchTermLower) && todayTicker !== searchTermLower;
            })
            .map((x) => ({ symbol: x.todayTicker, info: x }));
    }, [recentResultsEntries, dropdown.searchTerm]);

    const searchResults = useMemo<SymbolResult[]>(() => {
        if (!dropdown.searchTerm) {
            return [];
        }

        const recentSet = new Set(recentResults.map((x) => x.symbol.toLowerCase()));
        return symbolsMatchingPrefix.filter((x) => !recentSet.has(x.symbol.toLowerCase()));
    }, [dropdown.searchTerm, recentResults, symbolsMatchingPrefix]);

    // Create unified list with headers and items
    const allItems = useMemo(() => {
        const items: SymbolListItem[] = [];
        let currentIndex = 0;

        // Add direct search result if we have search text
        if (dropdown.searchTerm) {
            items.push({
                type: 'symbol',
                symbol: dropdown.searchTerm,
                info: directSymbolMatch || undefined,
                index: currentIndex++,
            });
        }

        // Add recent results with header
        if (recentResults.length > 0) {
            items.push({
                type: 'header',
                headerText: 'Recently Used',
            });

            recentResults.forEach((result) => {
                items.push({
                    type: 'recentSymbol',
                    symbol: result.symbol,
                    info: result.info,
                    index: currentIndex++,
                });
            });
        }

        // Add search results with header
        if (searchResults.length > 0) {
            items.push({
                type: 'header',
                headerText: 'Suggestions',
            });

            searchResults.forEach((result) => {
                items.push({
                    type: 'symbol',
                    symbol: result.symbol,
                    info: result.info,
                    index: currentIndex++,
                });
            });
        }

        return items;
    }, [dropdown.searchTerm, directSymbolMatch, recentResults, searchResults]);

    // Set up virtualizer
    const virtualizer = useVirtualizer({
        count: allItems.length,
        getScrollElement: () => virtualScrollRef.current,
        estimateSize: (index) => {
            const item = allItems[index];
            // Headers are shorter than regular items
            return item?.type === 'header' ? 12 : 16; // 3rem and 4rem respectively in pixels
        },
        // Add some overscan for smoother scrolling
        overscan: 5,
    });

    // Force virtualizer to remeasure when dropdown opens
    useEffect(() => {
        if (dropdownOpen && allItems.length > 0) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                virtualizer.measure();
            });
        }
    }, [dropdownOpen, allItems.length, virtualizer]);

    // Notify parent of available items when they change
    useEffect(() => {
        const symbols = allItems
            .filter((item): item is SymbolListItem & { symbol: string } => item.type !== 'header' && !!item.symbol)
            .map((item) => item.symbol);
        onDropdownItems('underlyingSymbol', symbols);
    }, [allItems, onDropdownItems]);

    // Sync active index with virtualizer when keyboard navigating
    useEffect(() => {
        if (dropdown.activeIndex >= 0) {
            // Find the visual index that corresponds to the logical active index
            const selectableItems = allItems.filter((item) => item.type !== 'header');
            const activeItem = selectableItems[dropdown.activeIndex];

            if (activeItem) {
                const visualIndex = allItems.findIndex(
                    (item) => item.type !== 'header' && item.index === activeItem.index,
                );

                if (visualIndex >= 0) {
                    virtualizer.scrollToIndex(visualIndex, {
                        align: 'auto',
                    });
                }
            }
        }
    }, [dropdown.activeIndex, allItems, virtualizer]);

    const handleInputClick = useEvent((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('underlyingSymbol');
    });

    const handleFocus = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFocused(true);
    });

    // blur is allowed to propagate to the parent container
    // unless the blur event comes from focus shifting to the dropdown
    const handleBlur = useEvent((e: React.FocusEvent) => {
        e.preventDefault();

        const next = e.relatedTarget;
        if (next && dialogRef.current?.contains(next)) {
            e.stopPropagation();
        } else {
            setIsFocused(false);
            onBlur?.(e);
        }
    });

    const handleInputChange = useEvent((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value.toUpperCase();
        onDropdownSearch(newValue);
    });

    const handleSelect = useEvent((symbol: string) => {
        onSelect(symbol);
    });

    const handleClearSymbol = useEvent(() => {
        if (onClear) {
            onClear();
        } else {
            onSelect('');
        }
    });

    const handleRemoveRecent = useEvent((symbol: string) => {
        recentlyUsedSymbolsService.remove(symbol);
    });

    // Display value: use search term if focused, otherwise show value
    const displayValue = isFocused ? dropdown.searchTerm : value;

    // Placeholder: show placeholder text
    const placeholderText = value || placeholder;

    return (
        <SymbolPickerMenu open={dropdownOpen}>
            <SymbolPickerMenuTrigger>
                <div className="relative flex h-5">
                    <input
                        tabIndex={tabIndex}
                        aria-label={ARIA_LABELS.underlyingSymbol}
                        placeholder={placeholderText}
                        value={displayValue}
                        onChange={handleInputChange}
                        onClick={handleInputClick}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                        className={cn(
                            'h-full w-16 bg-transparent px-0 text-xs outline-none dark:border-transparent',
                            className,
                            {
                                [inputErrorClass]: isInvalidSymbol,
                            },
                        )}
                    />
                    {value && clearable && <ClearButton onClick={handleClearSymbol} />}
                </div>
            </SymbolPickerMenuTrigger>
            <SymbolPickerMenuContent className="w-80 p-0" ref={dialogRef}>
                {allItems.length > 0 && (
                    <div
                        ref={virtualScrollRef}
                        className={cn(
                            'overflow-auto rounded-b',
                            allItems.length > SCROLL_THRESHOLD_ITEMS ? 'h-40' : 'h-auto',
                        )}
                    >
                        <div
                            style={{
                                height: `${virtualizer.getTotalSize()}px`,
                                width: '100%',
                                position: 'relative',
                            }}
                        >
                            {virtualizer.getVirtualItems().map((virtualItem) => {
                                const item = allItems[virtualItem.index];

                                return (
                                    <div
                                        key={virtualItem.key}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: `${virtualItem.size}px`,
                                            transform: `translateY(${virtualItem.start}px)`,
                                        }}
                                    >
                                        {item.type === 'header' ? (
                                            <SymbolPickerMenuHeader>{item.headerText}</SymbolPickerMenuHeader>
                                        ) : (
                                            <SymbolCommandItem
                                                index={item.index!}
                                                selected={dropdown.activeIndex === item.index}
                                                onSelect={handleSelect}
                                                onDropdownActiveIndex={onDropdownActiveIndex}
                                                result={{ symbol: item.symbol!, info: item.info }}
                                                onRemove={item.type === 'recentSymbol' ? handleRemoveRecent : undefined}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </SymbolPickerMenuContent>
        </SymbolPickerMenu>
    );
});

// Internal component for rendering symbol items
type SymbolCommandItemProps = {
    index: number;
    selected: boolean;
    result: SymbolResult;
    onDropdownActiveIndex: (index: number) => void;
    onSelect: (symbol: string) => void;
    onRemove?: (symbol: string) => void;
};

const SymbolCommandItem = ({
    index,
    selected,
    result,
    onSelect,
    onDropdownActiveIndex,
    onRemove,
}: SymbolCommandItemProps) => {
    const handleMouseEnter = useEvent(() => onDropdownActiveIndex(index));
    const handleSelect = useEvent(() => onSelect(result.symbol));
    const handleRemove = useEvent((e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove?.(result.symbol);
    });

    return (
        <SymbolPickerMenuItem
            selected={selected}
            onMouseEnter={handleMouseEnter}
            onSelect={handleSelect}
            className="flex h-4 w-full items-center gap-2 text-sm"
        >
            <div className="w-20 flex-none">{result.symbol}</div>
            <div className="flex-1 truncate">{result.info?.name || <>&ndash;</>}</div>
            {onRemove && <ClearButton onClick={handleRemove} className="static ml-auto p-0" />}
        </SymbolPickerMenuItem>
    );
};
